(function (root, factory) {
  const api = factory(root);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.SakSakEasterHunt = api;

    if (root.document) {
      if (root.document.readyState === 'loading') {
        root.document.addEventListener('DOMContentLoaded', api.init);
      } else {
        api.init();
      }

      root.document.addEventListener('shopify:section:load', api.init);
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  const DEFAULT_REQUIRED_EGG_COUNT = 3;
  const DEFAULT_REDIRECT_TARGET = '/collections/all';
  const POSITION_CLASS_PREFIX = 'easter-hunt-egg--';

  function normalizePath(path) {
    if (!path) return '/';

    let normalized = String(path).trim();

    try {
      if (/^https?:\/\//i.test(normalized)) {
        const absoluteUrl = new URL(normalized);
        normalized = absoluteUrl.pathname + absoluteUrl.search;
      }
    } catch (_error) {
      normalized = String(path).trim();
    }

    if (!normalized.startsWith('/')) {
      normalized = '/' + normalized;
    }

    if (normalized.length > 1 && normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized || '/';
  }

  function pathMatches(currentPath, egg) {
    const targetPath = normalizePath(egg && egg.targetPath);
    const matchType = (egg && egg.matchType) || 'exact';
    const normalizedCurrent = normalizePath(currentPath);
    const currentPathname = normalizedCurrent.split('?')[0];
    const targetPathname = targetPath.split('?')[0];

    if (matchType === 'contains') {
      return normalizedCurrent.indexOf(targetPath) !== -1;
    }

    return currentPathname === targetPathname;
  }

  function pickReward(config, randomValue) {
    const reward20Chance = Number(config && config.reward20Chance) || 0;
    const boundedChance = Math.max(0, Math.min(100, reward20Chance));
    const value = typeof randomValue === 'number' ? randomValue : Math.random();

    if (value < boundedChance / 100) {
      return (config && config.reward20Code) || 'Easter20';
    }

    return (config && config.reward15Code) || 'Easter15';
  }

  function reduceEggDiscovery(state, eggId, config, randomValue) {
    const currentState = state || {};
    const foundEggIds = Array.isArray(currentState.foundEggIds) ? currentState.foundEggIds.slice() : [];

    if (!eggId || currentState.unlocked || foundEggIds.indexOf(eggId) !== -1) {
      return {
        foundEggIds,
        rewardCode: currentState.rewardCode || '',
        unlocked: Boolean(currentState.unlocked),
      };
    }

    foundEggIds.push(eggId);

    const rewardCode = currentState.rewardCode || pickReward(config, randomValue);
    const requiredEggCount = Number(config && config.requiredEggCount) || DEFAULT_REQUIRED_EGG_COUNT;

    return {
      foundEggIds,
      rewardCode,
      unlocked: foundEggIds.length >= requiredEggCount,
    };
  }

  function buildDiscountUrl(rewardCode, redirectTarget) {
    if (!rewardCode) return '#';

    const normalizedTarget = normalizePath(redirectTarget || DEFAULT_REDIRECT_TARGET);
    return '/discount/' + encodeURIComponent(rewardCode) + '?redirect=' + encodeURIComponent(normalizedTarget);
  }

  function shouldShowBadge(state) {
    const foundEggIds = Array.isArray(state && state.foundEggIds) ? state.foundEggIds : [];
    return Boolean((state && state.unlocked) || foundEggIds.length > 0);
  }

  function parseConfig() {
    if (!root || !root.document) return null;

    const configElement = root.document.querySelector('[data-easter-hunt-config]');
    if (!configElement) return null;

    try {
      const parsed = JSON.parse(configElement.textContent || '{}');
      parsed.eggs = Array.isArray(parsed.eggs) ? parsed.eggs : [];
      return parsed;
    } catch (_error) {
      return null;
    }
  }

  function createDefaultState() {
    return {
      foundEggIds: [],
      rewardCode: '',
      unlocked: false,
    };
  }

  function isCampaignExpired(config) {
    if (!config || !config.endDate) return false;

    const endTimestamp = Date.parse(config.endDate);

    if (Number.isNaN(endTimestamp)) return false;

    return Date.now() > endTimestamp;
  }

  function sanitizeConfig(config) {
    const redirectTarget = normalizePath(config.redirectTarget || DEFAULT_REDIRECT_TARGET);
    const reward20Chance = Math.max(0, Math.min(100, Number(config.reward20Chance) || 20));
    const storageVersion = (config.storageVersion || '2026-easter-v1').trim();
    const requiredEggCount = Math.max(1, Number(config.requiredEggCount) || DEFAULT_REQUIRED_EGG_COUNT);

    return {
      enabled: Boolean(config.enabled),
      reward15Code: config.reward15Code || 'Easter15',
      reward20Code: config.reward20Code || 'Easter20',
      reward20Chance,
      redirectTarget,
      storageVersion,
      requiredEggCount,
      endDate: config.endDate || '',
      eggs: config.eggs.filter(function (egg) {
        return egg && egg.id && egg.targetPath;
      }),
    };
  }

  function getStorageKey(config) {
    return 'sak-sak-easter-hunt:' + config.storageVersion;
  }

  function loadState(config) {
    if (!root || !root.localStorage) return createDefaultState();

    try {
      const raw = root.localStorage.getItem(getStorageKey(config));
      if (!raw) return createDefaultState();

      const parsed = JSON.parse(raw);

      return {
        foundEggIds: Array.isArray(parsed.foundEggIds) ? parsed.foundEggIds : [],
        rewardCode: parsed.rewardCode || '',
        unlocked: Boolean(parsed.unlocked),
      };
    } catch (_error) {
      return createDefaultState();
    }
  }

  function saveState(config, state) {
    if (!root || !root.localStorage) return;

    root.localStorage.setItem(getStorageKey(config), JSON.stringify(state));
  }

  function findActiveEgg(config, state) {
    if (state.unlocked) return null;

    const currentPath = root.location.pathname + root.location.search;

    return config.eggs.find(function (egg) {
      return state.foundEggIds.indexOf(egg.id) === -1 && pathMatches(currentPath, egg);
    });
  }

  function getProgressText(state, config) {
    const progress = Math.min(state.foundEggIds.length, config.requiredEggCount);

    if (state.unlocked && state.rewardCode) {
      const percentage = state.rewardCode === config.reward20Code ? '20%' : '15%';
      return percentage + ' unlocked';
    }

    return progress + '/' + config.requiredEggCount + ' eggs found';
  }

  function getUnlockHeading(state, config) {
    if (state.rewardCode === config.reward20Code) {
      return 'You unlocked 20% off';
    }

    return 'You unlocked 15% off';
  }

  function getDefaultFoundMessage(state, config) {
    const progress = Math.min(state.foundEggIds.length, config.requiredEggCount);

    if (progress >= config.requiredEggCount) {
      return 'Final egg found. Your reward is ready.';
    }

    return 'Egg ' + progress + ' of ' + config.requiredEggCount + ' found.';
  }

  function createEggMarkup(egg) {
    const button = root.document.createElement('button');
    button.type = 'button';
    button.className = 'easter-hunt-egg ' + POSITION_CLASS_PREFIX + (egg.desktopPosition || 'bottom-right');
    button.setAttribute('aria-label', 'Hidden Easter egg');
    button.dataset.mobilePosition = egg.mobilePosition || 'bottom-right';
    button.dataset.eggId = egg.id;
    button.innerHTML = '<span class="easter-hunt-egg__shell" aria-hidden="true"></span>';
    return button;
  }

  function ensureUi(rootElement) {
    const badge = rootElement.querySelector('[data-easter-hunt-badge]');
    const toast = rootElement.querySelector('[data-easter-hunt-toast]');
    const modal = rootElement.querySelector('[data-easter-hunt-modal]');
    const modalTitle = rootElement.querySelector('[data-easter-hunt-modal-title]');
    const modalCode = rootElement.querySelector('[data-easter-hunt-modal-code]');
    const modalButton = rootElement.querySelector('[data-easter-hunt-modal-button]');

    return {
      badge,
      toast,
      modal,
      modalTitle,
      modalCode,
      modalButton,
    };
  }

  function renderBadge(ui, state, config) {
    if (!ui.badge) return;

    if (!shouldShowBadge(state)) {
      ui.badge.hidden = true;
      return;
    }

    ui.badge.hidden = false;
    ui.badge.textContent = getProgressText(state, config);
    ui.badge.setAttribute('data-unlocked', state.unlocked ? 'true' : 'false');
  }

  function showToast(ui, message) {
    if (!ui.toast) return;

    ui.toast.textContent = message;
    ui.toast.hidden = false;
    ui.toast.classList.add('is-visible');

    root.clearTimeout(showToast.hideTimeout);
    showToast.hideTimeout = root.setTimeout(function () {
      ui.toast.classList.remove('is-visible');
      root.setTimeout(function () {
        ui.toast.hidden = true;
      }, 200);
    }, 2600);
  }

  function openRewardModal(ui, state, config) {
    if (!ui.modal || !ui.modalTitle || !ui.modalCode || !ui.modalButton) return;

    ui.modal.hidden = false;
    ui.modal.setAttribute('aria-hidden', 'false');
    ui.modalTitle.textContent = getUnlockHeading(state, config);
    ui.modalCode.textContent = state.rewardCode;
    ui.modalButton.setAttribute('href', buildDiscountUrl(state.rewardCode, config.redirectTarget));
  }

  function closeRewardModal(ui) {
    if (!ui.modal) return;

    ui.modal.hidden = true;
    ui.modal.setAttribute('aria-hidden', 'true');
  }

  function setEggMobilePosition(eggElement) {
    if (!eggElement) return;

    const mobilePosition = eggElement.dataset.mobilePosition || 'bottom-right';

    eggElement.classList.forEach(function (className) {
      if (className.indexOf(POSITION_CLASS_PREFIX + 'mobile-') === 0) {
        eggElement.classList.remove(className);
      }
    });

    eggElement.classList.add(POSITION_CLASS_PREFIX + 'mobile-' + mobilePosition);
  }

  function mountEgg(rootElement, ui, egg, state, config) {
    const previousEgg = rootElement.querySelector('.easter-hunt-egg');
    if (previousEgg) previousEgg.remove();

    if (!egg) return;

    const eggButton = createEggMarkup(egg);
    setEggMobilePosition(eggButton);

    eggButton.addEventListener('click', function () {
      const nextState = reduceEggDiscovery(state, egg.id, config);

      if (nextState.foundEggIds.length === state.foundEggIds.length) {
        return;
      }

      state.foundEggIds = nextState.foundEggIds;
      state.rewardCode = nextState.rewardCode;
      state.unlocked = nextState.unlocked;
      saveState(config, state);
      renderBadge(ui, state, config);
      showToast(ui, egg.foundMessage || getDefaultFoundMessage(state, config));
      eggButton.remove();

      if (state.unlocked) {
        openRewardModal(ui, state, config);
      }
    });

    rootElement.appendChild(eggButton);
  }

  function initSingleRoot(rootElement) {
    if (!rootElement || rootElement.dataset.easterHuntReady === 'true') {
      return;
    }

    const config = sanitizeConfig(parseConfig() || {});

    if (!config.enabled || isCampaignExpired(config)) {
      return;
    }

    rootElement.dataset.easterHuntReady = 'true';

    const ui = ensureUi(rootElement);
    const state = loadState(config);
    const activeEgg = findActiveEgg(config, state);

    renderBadge(ui, state, config);
    mountEgg(rootElement, ui, activeEgg, state, config);

    if (ui.badge) {
      ui.badge.addEventListener('click', function () {
        if (state.unlocked) {
          openRewardModal(ui, state, config);
        }
      });
    }

    rootElement.querySelectorAll('[data-easter-hunt-close]').forEach(function (closeButton) {
      closeButton.addEventListener('click', function () {
        closeRewardModal(ui);
      });
    });

    if (ui.modal) {
      ui.modal.addEventListener('click', function (event) {
        if (event.target === ui.modal) {
          closeRewardModal(ui);
        }
      });
    }
  }

  function init() {
    if (!root || !root.document) return;

    root.document.querySelectorAll('[data-easter-hunt-root]').forEach(function (rootElement) {
      initSingleRoot(rootElement);
    });
  }

  return {
    buildDiscountUrl,
    init,
    pathMatches,
    pickReward,
    reduceEggDiscovery,
    shouldShowBadge,
  };
});
